import { useEffect, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import { MagnifyingGlass, X } from "@phosphor-icons/react";
import { api } from "../lib/api";

export default function CommandSearch({ open, onClose }) {
  const [q, setQ] = useState("");
  const [items, setItems] = useState([]);
  const [idx, setIdx] = useState(0);
  const inputRef = useRef(null);
  const navigate = useNavigate();

  useEffect(() => {
    if (open) setTimeout(() => inputRef.current?.focus(), 60);
    else {
      setQ("");
      setItems([]);
      setIdx(0);
    }
  }, [open]);

  useEffect(() => {
    if (!open) return;
    let cancel = false;
    const t = setTimeout(async () => {
      try {
        const rows = await api.listProducts({ q: q || undefined, limit: 8 });
        if (!cancel) {
          setItems(rows);
          setIdx(0);
        }
      } catch {}
    }, 180);
    return () => {
      cancel = true;
      clearTimeout(t);
    };
  }, [q, open]);

  const go = (id) => {
    onClose();
    navigate(`/product/${id}`);
  };

  const handleKey = (e) => {
    if (e.key === "ArrowDown") {
      e.preventDefault();
      setIdx((v) => Math.min(items.length - 1, v + 1));
    } else if (e.key === "ArrowUp") {
      e.preventDefault();
      setIdx((v) => Math.max(0, v - 1));
    } else if (e.key === "Enter") {
      if (items[idx]) go(items[idx].id);
      else if (q) {
        onClose();
        navigate(`/search?q=${encodeURIComponent(q)}`);
      }
    } else if (e.key === "Escape") {
      onClose();
    }
  };

  if (!open) return null;
  return (
    <div
      data-testid="command-search-overlay"
      className="fixed inset-0 z-50 bg-[#1C1B1A]/40 backdrop-blur-sm flex items-start justify-center pt-[12vh] px-4"
      onMouseDown={(e) => e.target === e.currentTarget && onClose()}
    >
      <div
        className="w-full max-w-2xl bg-white border border-[#1C1B1A]"
        role="dialog"
        aria-modal="true"
      >
        <div className="flex items-center gap-3 px-5 py-4 border-b border-[#E5E2DC]">
          <MagnifyingGlass size={18} className="text-[#6E6B65]" />
          <input
            ref={inputRef}
            data-testid="command-search-input"
            value={q}
            onChange={(e) => setQ(e.target.value)}
            onKeyDown={handleKey}
            placeholder="Search any cosmetic, fragrance, or perfume…"
            className="flex-1 bg-transparent text-base font-heading outline-none placeholder:text-[#6E6B65]"
          />
          <button
            type="button"
            onClick={onClose}
            data-testid="command-search-close"
            className="w-8 h-8 flex items-center justify-center text-[#6E6B65] hover:text-[#1C1B1A]"
            aria-label="Close"
          >
            <X size={16} />
          </button>
        </div>
        <div className="max-h-[50vh] overflow-y-auto thin-scroll">
          {items.length === 0 && (
            <div className="px-5 py-8 text-sm text-[#6E6B65]">
              Start typing to search products, brands, or ingredients.
            </div>
          )}
          {items.map((p, i) => (
            <button
              key={p.id}
              type="button"
              data-testid="command-search-result"
              onMouseEnter={() => setIdx(i)}
              onClick={() => go(p.id)}
              className={`w-full text-left flex items-center gap-4 px-5 py-3 border-b border-[#E5E2DC] last:border-b-0 transition-colors ${
                i === idx ? "bg-[#EFECE6]" : "bg-white"
              }`}
            >
              <div className="w-10 h-10 bg-[#EFECE6] overflow-hidden shrink-0">
                {p.image_url && (
                  <img src={p.image_url} alt="" className="w-full h-full object-cover" />
                )}
              </div>
              <div className="flex-1 min-w-0">
                <div className="eyebrow text-[9px]">{p.brand}</div>
                <div className="font-heading text-[15px] truncate">{p.name}</div>
              </div>
              <span className="text-[10px] uppercase tracking-wider text-[#6E6B65]">
                {p.category}
              </span>
            </button>
          ))}
        </div>
      </div>
    </div>
  );
}
